import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmAlertDialogImports } from '@spartan-ng/helm/alert-dialog';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { LoggerService } from '../../../../../services/logger.service';
import {
  VoucherDesign,
  VoucherDesignService,
  VoucherType,
  designErrorMessage,
  voucherTypeLabel,
} from '../../../../../services/voucher-design.service';
import { ActiveDesignsComponent } from '../active-designs/active-designs.component';
import { DesignLibraryComponent } from '../design-library/design-library.component';
import { NewDesignDialogComponent } from '../new-design-dialog/new-design-dialog.component';
import { SwitchDesignDialogComponent } from '../switch-design-dialog/switch-design-dialog.component';

/** What the confirmation dialog is about to do. */
type PendingAction =
  | { kind: 'activate'; design: VoucherDesign }
  | { kind: 'delete'; design: VoucherDesign }
  | null;

/**
 * The company's voucher designs on the settings tab: the two active slots, the
 * library, and the dialogs they share (new design, switch, confirm).
 */
@Component({
  selector: 'app-voucher-designs-panel',
  standalone: true,
  imports: [
    NgIcon,
    HlmAlertImports,
    HlmAlertDialogImports,
    HlmButtonImports,
    ActiveDesignsComponent,
    DesignLibraryComponent,
    NewDesignDialogComponent,
    SwitchDesignDialogComponent,
  ],
  providers: [provideIcons({ lucideTriangleAlert })],
  templateUrl: './voucher-designs-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block space-y-6' },
})
export class VoucherDesignsPanelComponent implements OnInit {
  private readonly designService = inject(VoucherDesignService);
  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService);

  readonly designs = signal<VoucherDesign[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly actionError = signal<string | null>(null);
  /** The design a request is running for; '*' while uploading. */
  readonly busyId = signal<string | null>(null);
  readonly previewingId = signal<string | null>(null);
  readonly pending = signal<PendingAction>(null);
  readonly switchSlot = signal<VoucherType | null>(null);
  readonly newDesignOpen = signal(false);

  readonly switchActiveId = computed(() => {
    const slot = this.switchSlot();
    return slot ? (this.designService.activeDesign(this.designs(), slot)?.public_id ?? null) : null;
  });
  readonly dialogTitle = computed(() => {
    const pending = this.pending();
    switch (pending?.kind) {
      case 'activate':
        return `„${pending.design.name}“ für ${voucherTypeLabel(pending.design.voucher_type)}e verwenden?`;
      case 'delete':
        return `Design „${pending.design.name}“ löschen?`;
      default:
        return '';
    }
  });
  readonly dialogText = computed(() => {
    const pending = this.pending();
    switch (pending?.kind) {
      case 'activate':
        return `Neue PDFs für ${voucherTypeLabel(pending.design.voucher_type)}e verwenden ab sofort dieses Design. Das bisherige Design bleibt gespeichert.`;
      case 'delete':
        return 'Das Design und sein Layout werden endgültig gelöscht.';
      default:
        return '';
    }
  });

  ngOnInit() {
    this.load();
  }

  load() {
    this.loading.set(this.designs().length === 0);
    this.error.set(null);
    this.designService.list().subscribe({
      next: ({ designs }) => {
        this.designs.set(designs);
        this.loading.set(false);
      },
      error: (error) => {
        this.logger.error('Designs konnten nicht geladen werden:', error);
        this.error.set('Deine Designs konnten nicht geladen werden.');
        this.loading.set(false);
      },
    });
  }

  // ------------------------------------------------------------- creating

  /** A new design goes straight to the editor. */
  onCreated(design: VoucherDesign) {
    this.newDesignOpen.set(false);
    this.router.navigate(['/voucher-designs', design.public_id]);
  }

  copy(event: { design: VoucherDesign; voucherType: VoucherType }) {
    this.actionError.set(null);
    this.busyId.set(event.design.public_id);
    const options =
      event.voucherType === event.design.voucher_type ? {} : { voucher_type: event.voucherType };
    this.designService.copy(event.design.public_id, options).subscribe({
      next: (copy) => {
        this.busyId.set(null);
        this.router.navigate(['/voucher-designs', copy.public_id]);
      },
      error: (error) => {
        this.logger.error('Kopieren fehlgeschlagen:', error);
        this.busyId.set(null);
        this.actionError.set(designErrorMessage(error, 'Das Design konnte nicht kopiert werden.'));
      },
    });
  }

  // ------------------------------------------------------------ switching

  openSwitch(voucherType: VoucherType) {
    this.switchSlot.set(voucherType);
  }

  closeSwitch() {
    this.switchSlot.set(null);
  }

  /** From the switch dialog: the choice is the confirmation. */
  chooseForSlot(design: VoucherDesign) {
    this.runActivate(design, () => this.switchSlot.set(null));
  }

  askActivate(design: VoucherDesign) {
    this.pending.set({ kind: 'activate', design });
  }

  askDelete(design: VoucherDesign) {
    this.pending.set({ kind: 'delete', design });
  }

  cancelPending() {
    this.pending.set(null);
  }

  /** Escape or a click on the backdrop closes the dialog: that is a cancel. */
  onDialogState(state: 'open' | 'closed') {
    if (state === 'closed' && this.pending()) this.cancelPending();
  }

  confirmPending() {
    const pending = this.pending();
    this.pending.set(null);
    if (!pending) return;
    if (pending.kind === 'activate') {
      this.runActivate(pending.design);
      return;
    }
    this.actionError.set(null);
    this.busyId.set(pending.design.public_id);
    this.designService.delete(pending.design.public_id).subscribe({
      next: () => {
        this.busyId.set(null);
        this.load();
      },
      error: (error) => {
        this.logger.error('Löschen fehlgeschlagen:', error);
        this.busyId.set(null);
        this.actionError.set(designErrorMessage(error, 'Das Design konnte nicht gelöscht werden.'));
      },
    });
  }

  private runActivate(design: VoucherDesign, done?: () => void) {
    this.actionError.set(null);
    this.busyId.set(design.public_id);
    this.designService.activate(design.public_id).subscribe({
      next: () => {
        this.busyId.set(null);
        done?.();
        this.load();
      },
      error: (error) => {
        this.logger.error('Aktivieren fehlgeschlagen:', error);
        this.busyId.set(null);
        this.actionError.set(
          designErrorMessage(error, 'Das Design konnte nicht übernommen werden.'),
        );
      },
    });
  }

  // -------------------------------------------------------------- preview

  previewPdf(design: VoucherDesign) {
    if (this.previewingId()) return;
    this.previewingId.set(design.public_id);
    this.actionError.set(null);
    this.designService.previewPdf(design.public_id).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `beispiel_${design.name}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        this.previewingId.set(null);
      },
      error: (error) => {
        this.logger.error('Vorschau fehlgeschlagen:', error);
        this.previewingId.set(null);
        this.actionError.set(
          designErrorMessage(error, 'Die Vorschau konnte nicht erstellt werden.'),
        );
      },
    });
  }
}
